import React, { useEffect, useState, useMemo, useRef } from "react";
import { useArtist } from "../../../context/ArtistContext";
import ShowAcceptDeclineModal from "../../../components/common/ShowAcceptDeclineModal";
import {
  acceptShow,
  declineShow,
  cancelShows,
  get,
  post,
  del,
} from "../../../services/api";
import BarContactsDropdown from "../../../components/messaging/BarContactsDropdown";
import ConfirmModal from "./ConfirmModal";
import { formatDateForInput } from "../../../utils/calendarDate";
import {
  formatCivilDateMedium,
  normalizeBarTimeZone,
  civilDateTodayInZone,
  parseCivilDateOnly,
} from "../../../utils/civilDateTime";

const formatTimeDisplay = (time) => {
  if (!time) return "";
  const [hoursStr, minutesStr] = time.split(":");
  const hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10) || 0;

  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return time;
  }

  const ampm = hours >= 12 ? "PM" : "AM";
  const normalizedHour = hours % 12 === 0 ? 12 : hours % 12;
  const paddedMinutes = minutes.toString().padStart(2, "0");

  return `${normalizedHour}:${paddedMinutes} ${ampm}`;
};

const formatCurrency = (amount) => {
  const value = Number(amount) || 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
};

const buildFormState = (source) => ({
  basePay:
    source?.base_pay !== undefined && source?.base_pay !== null
      ? String(source.base_pay)
      : "",
  tipsCash:
    source?.tips_cash !== undefined && source?.tips_cash !== null
      ? String(source.tips_cash)
      : "",
  tipsElectronic:
    source?.tips_electronic !== undefined && source?.tips_electronic !== null
      ? String(source.tips_electronic)
      : "",
});

const ShowCardModal = ({
  isOpen,
  onClose,
  showId,
  fallbackShow,
  onEarningsUpdate,
}) => {
  const { refreshShows } = useArtist();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showDetails, setShowDetails] = useState(null);
  const [formState, setFormState] = useState({
    basePay: "",
    tipsCash: "",
    tipsElectronic: "",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [showAcceptDeclineModal, setShowAcceptDeclineModal] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [responding, setResponding] = useState(false);
  const [showMessageDropdown, setShowMessageDropdown] = useState(false);
  const messageButtonRef = useRef(null);

  const placeholderShow = useMemo(() => {
    if (!fallbackShow) return null;
    return {
      bar_name: fallbackShow.bar_name,
      bar_id: fallbackShow.bar_id,
      show_id: fallbackShow.show_id,
      date: fallbackShow.date,
      start_time: fallbackShow.start_time,
      end_time: fallbackShow.end_time,
      base_pay: fallbackShow.base_pay,
      tips_cash: fallbackShow.tips_cash,
      tips_electronic: fallbackShow.tips_electronic,
      status: fallbackShow.status,
      stage_name: fallbackShow.stage_name,
    };
  }, [fallbackShow]);

  useEffect(() => {
    if (!isOpen) {
      setShowDetails(null);
      setError("");
      setLoading(false);
      setSaveMessage("");
      setIsEditing(false);
      setShowMessageDropdown(false);
      return;
    }

    // For artists, use fallbackShow data if available (shows already include earnings)
    // Only fetch if showId is provided but no fallbackShow
    if (fallbackShow) {
      setShowDetails(null); // Use placeholderShow instead
      setLoading(false);
      return;
    }

    // Only fetch if we have a showId but no fallbackShow
    if (!showId) {
      setLoading(false);
      return;
    }

    let isCancelled = false;

    const fetchShowDetails = async () => {
      try {
        setLoading(true);
        setError("");

        const data = await get(`/earnings/show/${showId}`);

        if (isCancelled) return;

        if (!data.success || !data.earnings) {
          throw new Error(data.error || "Unable to load show details");
        }

        setShowDetails(data.earnings);
        setFormState(buildFormState(data.earnings));
      } catch (fetchError) {
        console.error("Failed to fetch show earnings:", fetchError);
        if (!isCancelled) {
          setError(fetchError.message || "Failed to load show details.");
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchShowDetails();

    return () => {
      isCancelled = true;
    };
  }, [isOpen, showId, fallbackShow]);

  useEffect(() => {
    if (!isOpen) return;
    const source = showDetails || placeholderShow;
    // Exit edit mode if show becomes pending - check both placeholderShow and source
    const showStatus = placeholderShow?.status || source?.status;
    if (showStatus === "pending" && isEditing) {
      setIsEditing(false);
    }
    if (!isEditing && source) {
      setFormState(buildFormState(source));
    }
  }, [isOpen, isEditing, showDetails, placeholderShow]);

  if (!isOpen) {
    return null;
  }

  const detailSource = showDetails || placeholderShow;

  const venueTz = normalizeBarTimeZone(detailSource?.bar_time_zone);
  const todayYmd = civilDateTodayInZone(venueTz);
  const showYmd = parseCivilDateOnly(formatDateForInput(detailSource?.date));
  const isPastShow = Boolean(showYmd && showYmd < todayYmd);

  // Prefer status from placeholderShow (from shows list) if available, as it's more reliable
  const showStatus =
    placeholderShow?.status || showDetails?.status || detailSource?.status;
  const isPending = showStatus === "pending";
  const isAccepted = showStatus === "accepted";

  const barName = detailSource?.bar_name || "Venue";
  const stageName = detailSource?.stage_name || "—";
  const dateDisplay = formatCivilDateMedium(detailSource?.date, venueTz);
  const startTimeDisplay = formatTimeDisplay(detailSource?.start_time);
  const endTimeDisplay = formatTimeDisplay(detailSource?.end_time);
  const basePayDisplay = formatCurrency(detailSource?.base_pay ?? 0);
  const tipsCashDisplay = formatCurrency(detailSource?.tips_cash ?? 0);
  const tipsElectronicDisplay = formatCurrency(
    detailSource?.tips_electronic ?? 0,
  );

  const totalEarnings = isEditing
    ? Number.parseInt(formState.basePay || "0", 10) +
      Number.parseInt(formState.tipsCash || "0", 10) +
      Number.parseInt(formState.tipsElectronic || "0", 10)
    : (detailSource?.base_pay || 0) +
      (detailSource?.tips_cash || 0) +
      (detailSource?.tips_electronic || 0);
  const totalEarningsDisplay = formatCurrency(totalEarnings);

  const handleInputChange = (field) => (event) => {
    const { value } = event.target;
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
    setSaveMessage("");
  };

  const handleNumberInput = (field) => (event) => {
    const { value } = event.target;
    const sanitized = value.replace(/\D/g, "");
    setFormState((prev) => ({
      ...prev,
      [field]: sanitized,
    }));
    setSaveMessage("");
  };

  const handleModalClose = () => {
    if (isSaving) return;
    setIsEditing(false);
    setSaveMessage("");
    onClose();
  };

  const handleStartEdit = () => {
    const source = showDetails || placeholderShow;
    if (!source) return;
    // Only allow editing accepted shows - check both placeholderShow and source
    const showStatus = placeholderShow?.status || source?.status;
    if (showStatus !== "accepted") {
      return;
    }
    setFormState(buildFormState(source));
    setIsEditing(true);
    setSaveMessage("");
  };

  const handleCancelEdit = () => {
    const source = showDetails || placeholderShow;
    if (source) {
      setFormState(buildFormState(source));
    }
    setIsEditing(false);
    setSaveMessage("");
  };

  const handleSave = async (event) => {
    event.preventDefault();
    const detailSource = showDetails || placeholderShow;
    const currentShowId = showId || detailSource?.show_id;
    if (!currentShowId) return;
    setIsSaving(true);
    setSaveMessage("");
    setError("");

    try {
      const earningsPayload = {
        show_id: Number(currentShowId),
        base_pay: Number.parseInt(formState.basePay || "0", 10) || 0,
        tips_cash: Number.parseInt(formState.tipsCash || "0", 10) || 0,
        tips_electronic:
          Number.parseInt(formState.tipsElectronic || "0", 10) || 0,
      };

      const earningsData = await post("/earnings", earningsPayload);

      if (!earningsData.success) {
        throw new Error(earningsData.error || "Failed to update earnings");
      }

      setShowDetails((prev) => ({
        ...(prev || {}),
        ...earningsData.earnings,
        base_pay: earningsPayload.base_pay,
        tips_cash: earningsPayload.tips_cash,
        tips_electronic: earningsPayload.tips_electronic,
      }));

      setIsEditing(false);
      setSaveMessage("Changes saved successfully.");
      await refreshShows();
      // Refresh earnings overview if callback provided
      if (onEarningsUpdate) {
        onEarningsUpdate();
      }
    } catch (saveError) {
      console.error("Failed to save earnings:", saveError);
      setError(saveError.message || "Failed to save changes.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleAcceptDecline = async (action) => {
    const detailSource = showDetails || placeholderShow;
    const currentShowId = showId || detailSource?.show_id;
    if (!currentShowId || responding) return;

    try {
      setResponding(true);
      setError("");

      const result =
        action === "accept"
          ? await acceptShow(currentShowId)
          : await declineShow(currentShowId);

      if (result.success) {
        await refreshShows();
        if (onEarningsUpdate) {
          onEarningsUpdate();
        }
        onClose();
      } else {
        setError(result.error || `Failed to ${action} show`);
      }
    } catch (err) {
      setError(err.message || `Failed to ${action} show`);
    } finally {
      setResponding(false);
    }
  };

  const handleOpenAcceptDeclineModal = () => {
    setShowAcceptDeclineModal(true);
  };

  const handleCloseAcceptDeclineModal = () => {
    setShowAcceptDeclineModal(false);
    refreshShows();
    if (onEarningsUpdate) {
      onEarningsUpdate();
    }
  };

  const handleCancelShow = async () => {
    if (cancelling || !detailSource) return;

    try {
      setCancelling(true);
      setError(null);

      const showIdToCancel = detailSource.show_id;
      const recurringGroupId = detailSource.recurring_group_id;

      const result = await cancelShows([showIdToCancel], recurringGroupId);

      if (result.success) {
        refreshShows();
        if (onEarningsUpdate) {
          onEarningsUpdate();
        }
        window.dispatchEvent(new CustomEvent("showsUpdated"));
        setShowCancelConfirm(false);
        onClose();
      } else {
        setError(result.error || "Failed to cancel show");
      }
    } catch (err) {
      setError(err.message || "Failed to cancel show");
    } finally {
      setCancelling(false);
    }
  };

  const handleRemoveFromHistory = async () => {
    const currentShowId = showId || detailSource?.show_id;
    if (!currentShowId || isRemoving) return;
    try {
      setIsRemoving(true);
      setError(null);
      const data = await del(`/shows/${currentShowId}`);
      if (!data.success) {
        throw new Error(data.error || "Failed to remove show");
      }
      await refreshShows();
      if (onEarningsUpdate) {
        onEarningsUpdate();
      }
      setShowRemoveConfirm(false);
      onClose();
    } catch (err) {
      setError(err.message || "Failed to remove from history");
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <>
      {showAcceptDeclineModal && (
        <ShowAcceptDeclineModal
          isOpen={showAcceptDeclineModal}
          onClose={handleCloseAcceptDeclineModal}
          showId={showId || detailSource?.show_id}
          onSuccess={handleCloseAcceptDeclineModal}
        />
      )}
      <div className="show-card-modal-overlay" role="presentation">
        <div
          className="show-card-modal show-card-modal--onboarding-style"
          role="dialog"
          aria-modal="true"
          aria-labelledby="show-card-modal-title"
        >
          <div className="artist-profile-modal-header">
            <h2 id="show-card-modal-title">Show Details</h2>
            <div className="artist-profile-modal-header-actions">
              <button
                ref={messageButtonRef}
                type="button"
                className="artist-profile-message-btn"
                onClick={() => setShowMessageDropdown(true)}
                aria-label="Message bar contacts"
                disabled={isSaving || !detailSource?.bar_id}
              >
                💬
              </button>
              <button
                type="button"
                className="artist-profile-modal-close"
                aria-label="Close show details modal"
                onClick={handleModalClose}
                disabled={isSaving}
              >
                ×
              </button>
            </div>
          </div>
          {showMessageDropdown && (
            <BarContactsDropdown
              isOpen={showMessageDropdown}
              onClose={() => setShowMessageDropdown(false)}
              barId={detailSource?.bar_id}
              buttonRef={messageButtonRef}
            />
          )}
          {loading && !fallbackShow ? (
            <div className="show-card-modal-body show-card-modal-body--loading">
              <div className="show-card-modal-loading show-card-modal-loading--centered">
                <span className="spinner" aria-hidden="true" />
                <span>Loading show details…</span>
              </div>
            </div>
          ) : isEditing && !isPending ? (
            <form
              className="show-card-modal-form show-card-modal-form--onboarding"
              onSubmit={handleSave}
            >
              <div className="show-card-modal-body show-card-modal-body--onboarding">
                <div className="onboarding-section show-card-modal-section">
                  <h2 className="section-title">Show Details</h2>
                  <div className="form-grid">
                    <div className="input-group">
                      <label className="input-label">Venue</label>
                      <input
                        type="text"
                        className="settings-input"
                        value={barName || "—"}
                        readOnly
                        disabled
                        aria-readonly="true"
                      />
                    </div>
                    <div className="input-group">
                      <label className="input-label">Artist</label>
                      <input
                        type="text"
                        className="settings-input"
                        value={stageName || "—"}
                        readOnly
                        disabled
                        aria-readonly="true"
                      />
                    </div>
                    <div className="input-group">
                      <label className="input-label">Date</label>
                      <input
                        type="text"
                        className="settings-input"
                        value={dateDisplay || "—"}
                        readOnly
                        disabled
                        aria-readonly="true"
                      />
                    </div>
                    <div className="input-group">
                      <label className="input-label">Time</label>
                      <input
                        type="text"
                        className="settings-input"
                        value={
                          startTimeDisplay && endTimeDisplay
                            ? `${startTimeDisplay} - ${endTimeDisplay}`
                            : "—"
                        }
                        readOnly
                        disabled
                        aria-readonly="true"
                      />
                    </div>
                  </div>
                </div>

                <div className="onboarding-section show-card-modal-section">
                  <h2 className="section-title">Earnings</h2>
                  <div className="form-grid">
                    <div className="input-group">
                      <label
                        className="input-label"
                        htmlFor="show-card-base-pay"
                      >
                        Base Pay
                      </label>
                      <div className="show-card-modal-currency-input">
                        <span className="show-card-modal-currency-prefix">
                          $
                        </span>
                        <input
                          id="show-card-base-pay"
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          className="settings-input"
                          value={formState.basePay}
                          onChange={handleNumberInput("basePay")}
                          disabled={isSaving}
                          placeholder={detailSource?.base_pay ? String(detailSource.base_pay) : "0"}
                        />
                      </div>
                    </div>
                    <div className="input-group">
                      <label
                        className="input-label"
                        htmlFor="show-card-tips-cash"
                      >
                        Cash Tips
                      </label>
                      <div className="show-card-modal-currency-input">
                        <span className="show-card-modal-currency-prefix">
                          $
                        </span>
                        <input
                          id="show-card-tips-cash"
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          className="settings-input"
                          value={formState.tipsCash}
                          onChange={handleNumberInput("tipsCash")}
                          disabled={isSaving}
                          placeholder={detailSource?.tips_cash ? String(detailSource.tips_cash) : "0"}
                        />
                      </div>
                    </div>
                    <div className="input-group">
                      <label
                        className="input-label"
                        htmlFor="show-card-tips-electronic"
                      >
                        Electronic Tips
                      </label>
                      <div className="show-card-modal-currency-input">
                        <span className="show-card-modal-currency-prefix">
                          $
                        </span>
                        <input
                          id="show-card-tips-electronic"
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          className="settings-input"
                          value={formState.tipsElectronic}
                          onChange={handleNumberInput("tipsElectronic")}
                          disabled={isSaving}
                          placeholder={detailSource?.tips_electronic ? String(detailSource.tips_electronic) : "0"}
                        />
                      </div>
                    </div>
                    <div className="input-group show-card-modal-total-row">
                      <span className="input-label">Total Earnings</span>
                      <span className="show-card-modal-total-value">
                        {totalEarningsDisplay}
                      </span>
                    </div>
                  </div>
                </div>

                {error && (
                  <div className="show-card-modal-error" role="alert">
                    {error}
                  </div>
                )}
              </div>

              <div className="onboarding-actions show-card-modal-footer--onboarding">
                <button
                  type="button"
                  className="show-card-modal-cancel-btn"
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={isSaving || loading}
                >
                  {isSaving ? "Saving…" : "Save Changes"}
                </button>
              </div>
            </form>
          ) : detailSource ? (
            <>
              <div className="show-card-modal-body show-card-modal-body--onboarding">
                <div className="onboarding-section show-card-modal-section">
                  <h2 className="section-title">Show Details</h2>
                  <div className="form-grid">
                    <div className="input-group">
                      <label className="input-label">Venue</label>
                      <input
                        type="text"
                        className="settings-input"
                        value={barName || "—"}
                        readOnly
                        disabled
                        aria-readonly="true"
                      />
                    </div>
                    <div className="input-group">
                      <label className="input-label">Artist</label>
                      <input
                        type="text"
                        className="settings-input"
                        value={stageName || "—"}
                        readOnly
                        disabled
                        aria-readonly="true"
                      />
                    </div>
                    <div className="input-group">
                      <label className="input-label">Date</label>
                      <input
                        type="text"
                        className="settings-input"
                        value={dateDisplay || "—"}
                        readOnly
                        disabled
                        aria-readonly="true"
                      />
                    </div>
                    <div className="input-group">
                      <label className="input-label">Time</label>
                      <input
                        type="text"
                        className="settings-input"
                        value={
                          startTimeDisplay && endTimeDisplay
                            ? `${startTimeDisplay} - ${endTimeDisplay}`
                            : "—"
                        }
                        readOnly
                        disabled
                        aria-readonly="true"
                      />
                    </div>
                  </div>
                </div>

                <div className="onboarding-section show-card-modal-section">
                  <h2 className="section-title">Earnings</h2>
                  <div className="form-grid">
                    <div className="input-group">
                      <span className="input-label">Base Pay</span>
                      <input
                        type="text"
                        className="settings-input"
                        value={basePayDisplay}
                        readOnly
                        disabled
                        aria-readonly="true"
                      />
                    </div>
                    <div className="input-group">
                      <span className="input-label">Cash Tips</span>
                      <input
                        type="text"
                        className="settings-input"
                        value={tipsCashDisplay}
                        readOnly
                        disabled
                        aria-readonly="true"
                      />
                    </div>
                    <div className="input-group">
                      <span className="input-label">Electronic Tips</span>
                      <input
                        type="text"
                        className="settings-input"
                        value={tipsElectronicDisplay}
                        readOnly
                        disabled
                        aria-readonly="true"
                      />
                    </div>
                    <div className="input-group show-card-modal-total-row">
                      <span className="input-label">Total Earnings</span>
                      <span className="show-card-modal-total-value">
                        {totalEarningsDisplay}
                      </span>
                    </div>
                  </div>
                </div>

                {error && (
                  <div className="show-card-modal-error" role="alert">
                    {error}
                  </div>
                )}

                {saveMessage && !error && (
                  <div className="show-card-modal-success" role="status">
                    {saveMessage}
                  </div>
                )}

                {showCancelConfirm && (
                  <div className="onboarding-section show-card-modal-section show-card-modal-confirm">
                    <h3 className="section-title">Confirm Cancellation</h3>
                    <p className="section-description">
                      Are you sure you want to cancel this show? The bar owner
                      will be notified.
                    </p>
                    <div className="show-card-modal-confirm-actions">
                      <button
                        type="button"
                        className="show-card-modal-cancel-btn"
                        onClick={() => {
                          setShowCancelConfirm(false);
                          setError(null);
                        }}
                        disabled={cancelling}
                      >
                        No, Keep Show
                      </button>
                      <button
                        type="button"
                        className="danger-btn"
                        onClick={handleCancelShow}
                        disabled={cancelling}
                      >
                        {cancelling ? "Cancelling..." : "Yes, Cancel Show"}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="onboarding-actions show-card-modal-footer--onboarding">
                {isPending ? (
                  <>
                    <button
                      type="button"
                      className="show-card-modal-cancel-btn"
                      onClick={() => handleAcceptDecline("decline")}
                      disabled={responding}
                    >
                      {responding ? "Processing..." : "Decline"}
                    </button>
                    <button
                      type="button"
                      className="submit-btn"
                      onClick={() => handleAcceptDecline("accept")}
                      disabled={responding}
                    >
                      {responding ? "Processing..." : "Accept"}
                    </button>
                  </>
                ) : (
                  <>
                    {isAccepted && (
                      <>
                        {!isPastShow && (
                          <button
                            type="button"
                            className="submit-btn"
                            onClick={() => setShowCancelConfirm(true)}
                            disabled={loading || !detailSource || cancelling}
                          >
                            Cancel Show
                          </button>
                        )}
                        {isPastShow && (
                          <button
                            type="button"
                            className="danger-btn"
                            onClick={() => setShowRemoveConfirm(true)}
                            disabled={loading || !detailSource || isRemoving}
                          >
                            {isRemoving ? "Removing…" : "Remove from my history"}
                          </button>
                        )}
                        <button
                          type="button"
                          className="danger-btn"
                          onClick={handleStartEdit}
                          disabled={loading || !detailSource}
                        >
                          Edit Earnings
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      className="show-card-modal-cancel-btn"
                      onClick={handleModalClose}
                      disabled={isSaving}
                    >
                      Close
                    </button>
                  </>
                )}
              </div>
            </>
          ) : (
            <div className="show-card-modal-body show-card-modal-body--loading">
              <div className="show-card-modal-loading show-card-modal-loading--centered">
                <span>No show data available</span>
              </div>
            </div>
          )}
        </div>
      </div>
      <ConfirmModal
        open={showRemoveConfirm}
        title="Remove from my history"
        message="This will only remove the show from your view and earnings. The bar will still see it. This cannot be undone."
        confirmLabel="Remove"
        cancelLabel="Cancel"
        isConfirming={isRemoving}
        onConfirm={handleRemoveFromHistory}
        onCancel={() => {
          if (!isRemoving) setShowRemoveConfirm(false);
        }}
      />
    </>
  );
};

export default ShowCardModal;
